import React, { useRef, useEffect, useState } from "react";
import styled, { keyframes, css, useTheme } from "styled-components";
import { PopperProps } from "@mui/material";
import { triggerHaptic } from "tools/haptic";
import { Trans } from "react-i18next";
import ArrowPopper from "../other/ArrowPopper";

type Props = {
  open: boolean;
  textToSay: string;
  anchorEl: PopperProps["anchorEl"];
  onClose?: () => void;
};

const SHAKE_DURATION = 600;

const shakeAnim = keyframes`
  10%, 90% { transform: translateX(-50%) translate3d(-2px, 0, 0); }
  20%, 80% { transform: translateX(-50%) translate3d(4px, 0, 0); }
  30%, 50%, 70% { transform: translateX(-50%) translate3d(-8px, 0, 0); }
  40%, 60% { transform: translateX(-50%) translate3d(8px, 0, 0); }
`;

const TooltipWrapper = styled.div<{ $shake: boolean }>`
  ${({ $shake }) =>
    $shake &&
    css`
      animation-name: ${shakeAnim};
      animation-duration: ${SHAKE_DURATION}ms;
      animation-timing-function: ease-in-out;
      animation-iteration-count: 1;
      animation-fill-mode: both;
    `}
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
`;

const Title = styled.div`
  font-size: 16px;
  margin-bottom: 8px;
`;

const Highlighted = styled.strong`
  font-weight: 700;
`;

const Subtext = styled.div`
  font-size: 13px;
  opacity: 0.95;
  line-height: 1.3;
`;

const PronounceHint: React.FC<Props> = ({ open, anchorEl, onClose, textToSay }) => {
  const [shakeCount, setShakeCount] = useState(0);
  const popperContentRef = useRef<HTMLDivElement>(null);
  const theme = useTheme();

  useEffect(() => {
    if (!open) return;
    function blockEvent(event: Event) {
      if (anchorEl instanceof Element && event.target instanceof Node && anchorEl.contains(event.target)) {
        onClose?.();
      } else if (popperContentRef.current && !popperContentRef.current.contains(event.target as Node)) {
        setShakeCount((c) => c + 1);
        triggerHaptic();
        event.preventDefault();
        event.stopPropagation(); // block others
      }
    }
    document.addEventListener("mousedown", blockEvent, true);
    document.addEventListener("click", blockEvent, true);
    document.addEventListener("pointerdown", blockEvent, true);
    document.addEventListener("touchstart", blockEvent, true);
    return () => {
      document.removeEventListener("mousedown", blockEvent, true);
      document.removeEventListener("click", blockEvent, true);
      document.removeEventListener("pointerdown", blockEvent, true);
      document.removeEventListener("touchstart", blockEvent, true);
    };
  }, [open, anchorEl, onClose]);

  // Ensure anchorEl is HTMLElement | null for ArrowPopper
  const anchorElement =
    anchorEl && typeof anchorEl !== "function" && (anchorEl as HTMLElement).nodeType === 1
      ? (anchorEl as HTMLElement)
      : null;

  return (
    <ArrowPopper
      open={open && !!anchorElement}
      anchorEl={anchorElement}
      bgColor={theme.duo.palette.blueMain}
      color={theme.duo.palette.white}
      onClose={onClose}
      shake={shakeCount > 0}
      shakeKey={shakeCount}
      contentRef={popperContentRef}
    >
      <TooltipWrapper role="dialog" aria-modal="true" $shake={false}>
        <Content>
          <Title>
            <>
              <Trans>🎤 tap and say:</Trans> <Highlighted>{textToSay}</Highlighted>
            </>
          </Title>
          <Subtext>
            <Trans>Get instant feedback on your pronunciation!</Trans>
          </Subtext>
        </Content>
      </TooltipWrapper>
    </ArrowPopper>
  );
};

export default PronounceHint;
